import React from "react";
import { useTranslation } from "../context/LanguageContext";

interface ErrorStateProps {
  title?: string;
  message: string;
  onRetry?: () => void;
  retryLabel?: string;
  className?: string;
}

const ErrorState: React.FC<ErrorStateProps> = ({
  title,
  message,
  onRetry,
  retryLabel,
  className = "",
}) => {
  const { t } = useTranslation();
  return (
    <div
      className={`flex flex-col items-center justify-center h-full min-h-64 p-8 text-center ${className}`}
    >
      {/* Modern error icon with gradient background */}
      <div className="relative mb-6">
        <div className="w-20 h-20 bg-gradient-to-br from-red-100 to-red-200 rounded-full flex items-center justify-center">
          <svg
            className="w-10 h-10 text-red-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.664-.833-2.464 0L4.35 16.5c-.77.833.192 2.5 1.732 2.5z"
            />
          </svg>
        </div>
        {/* Subtle pulsing ring */}
        <div className="absolute inset-0 w-20 h-20 bg-red-400 rounded-full animate-ping opacity-20"></div>
      </div>

      {/* Error content with better typography */}
      <div className="space-y-4 max-w-md">
        <h3 className="text-xl font-semibold text-gray-900">
          {title || t("common.somethingWentWrong")}
        </h3>
        <p className="text-gray-600 leading-relaxed">{message}</p>
      </div>

      {/* Modern retry button */}
      {onRetry && (
        <button
          onPointerDown={onRetry}
          className="mt-6 inline-flex items-center px-6 py-3 bg-gradient-to-r from-red-600 to-red-700 text-white font-medium rounded-lg hover:from-red-700 hover:to-red-800 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-all duration-200 shadow-sm hover:shadow-md transform hover:scale-105"
        >
          <svg
            className="w-4 h-4 mr-2"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
            />
          </svg>
          {retryLabel || t("common.tryAgain")}
        </button>
      )}

      {/* Subtle background decoration */}
      <div className="absolute inset-0 -z-10 opacity-5">
        <div className="absolute top-1/4 right-1/4 w-32 h-32 bg-red-400 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 left-1/4 w-48 h-48 bg-red-300 rounded-full blur-3xl"></div>
      </div>
    </div>
  );
};

export default ErrorState;
